import { FC, ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogTitle
} from "src/components/ui/dialog";
import { Button, ButtonProps } from "src/components/ui/button";

interface ConfirmationButtonProps extends Omit<ButtonProps, "children"> {
	children?: ReactNode;
	isLoading?: boolean;
}

export interface ConfirmationModalProps {
	isOpen: boolean;
	title?: string;

	children?: ReactNode;

	description?: string | JSX.Element;
	descriptionProps?: Record<string, unknown>;

	icon?: JSX.Element;

	cancelBtnProps?: ConfirmationButtonProps;
	submitBtnProps?: ConfirmationButtonProps;

	onCancel: () => void;
	onSubmit: () => void;
}

export const ConfirmationModal: FC<ConfirmationModalProps> = ({
	title,
	children,
	description,
	cancelBtnProps,
	submitBtnProps,
	onCancel,
	onSubmit,
	icon,
	isOpen
}) => {
	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) onCancel();
			}}
		>
			<DialogContent className="max-w-[481px] p-8">
				<div className="flex flex-col items-center">
					{icon ? (
						<div className="text-zinc-500 mb-4">{icon}</div>
					) : (
						<div className="text-zinc-500 mb-4">
							<AlertTriangle className="h-10 w-10" data-testid="warning-icon" />
						</div>
					)}

					{title && (
						<DialogTitle className="text-center mb-2 font-heading text-lg font-semibold text-foreground">
							{title}
						</DialogTitle>
					)}

					{description && typeof description === "string" ? (
						<p className="text-sm text-zinc-600 text-center mt-2 leading-relaxed">
							{description}
						</p>
					) : (
						description
					)}

					{children}
				</div>

				<DialogFooter className="flex flex-row gap-3 mt-8 sm:justify-between sm:space-x-0">
					<Button
						variant="outline"
						size="lg"
						className="flex-1"
						onClick={onCancel}
						disabled={cancelBtnProps?.isLoading || cancelBtnProps?.disabled}
					>
						{cancelBtnProps?.isLoading
							? "Loading..."
							: (cancelBtnProps?.children ?? "Cancel")}
					</Button>

					<Button
						variant="default"
						size="lg"
						className="flex-1"
						onClick={onSubmit}
						disabled={submitBtnProps?.isLoading || submitBtnProps?.disabled}
					>
						{submitBtnProps?.isLoading
							? "Loading..."
							: (submitBtnProps?.children ?? "Yes")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
